import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@components/ui/popover';
import { Skeleton } from '@components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { HTTPWrapper } from '@/utils/http';
import { VersionBadge } from '../components/VersionBadge';
import { GROUP_LABELS } from '../constants.workspace';
import type { RegistryTag } from '../workspace.types';

type Props = {
  processId: string;
  value: RegistryTag | null;
  onChange: (tag: RegistryTag | null) => void;
  compact?: boolean;
  error?: boolean;
};

export const VersionPicker = ({
  processId,
  value,
  onChange,
  compact,
  error
}: Props) => {
  const [registryTags, setRegistryTags] = useState<RegistryTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [open, setOpen] = useState(false);

  const http = useMemo(() => new HTTPWrapper<unknown>('process'), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset fetched state when processId changes
  useEffect(() => {
    setFetched(false);
    setRegistryTags([]);
  }, [processId]);

  const fetchTags = useCallback(async () => {
    if (fetched) {
      return;
    }
    setLoading(true);
    try {
      const tags = await http.get<RegistryTag[]>(`/${processId}/registry/tags`);
      setRegistryTags(tags ?? []);
    } catch {
      // GitLab repo may not be configured
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [processId, fetched, http]);

  useEffect(() => {
    if (open) {
      fetchTags();
    }
  }, [open, fetchTags]);

  const sortByDate = (a: RegistryTag, b: RegistryTag) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const grouped = useMemo(
    () => ({
      stable: registryTags.filter(t => t.group === 'stable').sort(sortByDate),
      candidate: registryTags
        .filter(t => t.group === 'candidate')
        .sort(sortByDate),
      dev: registryTags.filter(t => t.group === 'dev').sort(sortByDate)
    }),
    [registryTags]
  );

  let defaultTab = value?.group ?? 'stable';

  if (grouped.stable.length > 0) {
    defaultTab = 'stable';
  } else if (grouped.candidate.length > 0) {
    defaultTab = 'candidate';
  } else if (grouped.dev.length > 0) {
    defaultTab = 'dev';
  }

  const renderTagList = (tags: RegistryTag[]) => {
    if (tags.length === 0) {
      return (
        <p className="p-3 text-center text-muted-foreground text-xs">
          No tags in this group
        </p>
      );
    }
    return (
      <div className="flex max-h-48 flex-col overflow-y-auto">
        {tags.map(tag => (
          <button
            className="w-full py-[1px]"
            key={tag.name}
            onClick={() => {
              onChange({
                name: tag.name,
                displayName: tag.displayName,
                type: tag.type,
                group: tag.group,
                createdAt: new Date(tag.createdAt).toISOString()
              });
              setOpen(false);
            }}
            type="button"
          >
            <div
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                tag.name === value?.name && 'bg-accent font-medium'
              )}
            >
              <div className="min-w-0 flex-1">
                <VersionBadge version={tag} />
              </div>
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                {new Date(tag.createdAt).toLocaleDateString()}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          aria-label={
            value ? `Version: ${value.displayName}` : 'Use latest version'
          }
          className={cn(
            'shrink-0',
            compact
              ? 'inline-flex items-center p-0'
              : cn(
                  'inline-flex items-center justify-between rounded-md border border-input px-3 font-normal shadow-sm hover:bg-accent hover:text-accent-foreground',
                  'h-8 w-full',
                  error && 'border-destructive text-destructive'
                )
          )}
          type="button"
        >
          <VersionBadge chevron open={open} version={value} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {loading ? (
          <div className="flex flex-col gap-1 p-3">
            <div className="mb-2 flex gap-2">
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-7 w-24" />
              <Skeleton className="h-7 w-16" />
            </div>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : (
          <Tabs defaultValue={defaultTab}>
            <TabsList className="w-full rounded-none rounded-b-md border-border border-b">
              {(['stable', 'candidate', 'dev'] as const).map(group => (
                <TabsTrigger
                  className="flex-1 text-xs"
                  key={group}
                  value={group}
                >
                  {GROUP_LABELS[group]}
                  {grouped[group].length > 0 && (
                    <Badge
                      className="ml-1.5 flex h-4 min-w-4 items-center justify-center px-1 text-[10px]"
                      variant="secondary"
                    >
                      {grouped[group].length}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {(['stable', 'candidate', 'dev'] as const).map(group => (
              <TabsContent className="p-1" key={group} value={group}>
                {renderTagList(grouped[group])}
              </TabsContent>
            ))}
          </Tabs>
        )}
        {value && (
          <div className="flex justify-end border-t p-1">
            <button
              className="rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              type="button"
            >
              Use Latest
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
